import {Injectable, inject} from '@angular/core';

import {Observable, Subject, defer, merge, throwError, timer} from 'rxjs';
import {
  filter,
  finalize,
  first,
  ignoreElements,
  last,
  map,
  switchMap,
  takeWhile,
  tap,
  timeout,
} from 'rxjs/operators';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import {KeyUtilsPL2 as KU} from '@common/utils/dist/utils/key-utils-pl2.js';

import {Action} from '@actions/action';
import {ActionUtils as AU} from '@actions/action-utils';
import {
  AddProtoCardToLessonAction,
  LESSON_CARD_TYPES,
} from '@actions/add-proto-card-to-lesson.action';
import {SendAgentMessageAction} from '@actions/send-agent-message.action';
import {AgentMemoryStore, SessionState} from '@stores/agent-memory.store';
import {EntryStore} from '@stores/entry.store';
import {UserStore} from '@stores/user.store';
import {LessonBuildEvent, LessonSource} from '@models/lesson-generation-types';
import {lessonChatSessionId} from '@utils/ai-utils';
import {buildLessonMessage, lessonTitle} from '@utils/lesson-generation-utils';

// The server confirms an attached document within seconds
const ATTACH_TIMEOUT_MS = 60_000;
// The server checks the extractor only when asked; as often as the chat panel asks
const DOCUMENT_POLL_MS = 15_000;
// Reading the PDF (the server gives up on a document after 20 minutes)
const READ_TIMEOUT_MS = 30 * 60 * 1000;
// The agent's turn, including any resumes
const BUILD_TIMEOUT_MS = 20 * 60 * 1000;

/**
 * Builds a lesson from an uploaded PDF with the lesson chat agent (approach A: runs in this
 * tab). Creates a lesson named after the file, attaches the PDF to that lesson's chat, waits
 * until it has been read, asks the agent to turn it into cards and adds each card to the lesson
 * as it arrives. Emits how much of the PDF has been read, then the lesson. The conversation
 * stays with the lesson, so its chat can carry on from there.
 */
@Injectable({providedIn: 'root'})
export class BuildLessonFromPdfAction implements Action<
  LessonSource,
  LessonBuildEvent
> {
  private readonly _entryStore = inject(EntryStore);
  private readonly _userStore = inject(UserStore);
  private readonly _memoryStore = inject(AgentMemoryStore);
  private readonly _sendAction = inject(SendAgentMessageAction);
  private readonly _addCardAction = inject(AddProtoCardToLessonAction);

  execute(source: LessonSource): Observable<LessonBuildEvent> {
    return defer(() => {
      const userId = this._userStore.state()?.userId;
      if (!userId) {
        return throwError(() => new Error('Not signed in'));
      }
      const title = lessonTitle(source.fileName);
      const lesson = this._entryStore.create(
        AU.buildCreateLabReportRequest(false, title),
      )?.[0];
      if (!lesson) {
        return throwError(() => new Error('Could not create the lesson'));
      }
      const lessonPK = KU.stringFromKey({mId: lesson.mId, sK: lesson.sK});
      const sid = lessonChatSessionId(userId, lessonPK);
      if (!this._memoryStore.isLoaded(sid)) {
        this._memoryStore.initSession(sid);
        this._memoryStore.markLoaded(sid);
      }

      this._sendAction.attachDocument(sid, source.fileKey, source.fileName);
      const reading = new Subject<number>();
      const built$ = this._waitUntilRead(sid, source.fileKey, (fraction) =>
        reading.next(fraction),
      ).pipe(
        tap(() => {
          const {message, display} = buildLessonMessage(source.fileName);
          this._sendAction.sendForLesson(sid, lessonPK, message, display);
        }),
        switchMap(() => this._addCardsAsTheyArrive(sid, lessonPK)),
        map(
          (): LessonBuildEvent => ({
            lesson: {title, mId: lesson.mId, sK: lesson.sK},
          }),
        ),
        finalize(() => reading.complete()),
      );
      return merge(
        reading.pipe(
          map((fraction): LessonBuildEvent => ({reading: fraction})),
        ),
        built$,
      );
    });
  }

  /**
   * Waits until the server says the attached PDF has been read, as the chat panel does: the
   * server checks the extractor's results on each request, so it is asked periodically.
   */
  private _waitUntilRead(
    sid: string,
    fileKey: string,
    onReading: (fraction: number) => void,
  ): Observable<void> {
    const document$ = this._session$(sid).pipe(
      map((s) =>
        (s.agentContext?.documents ?? []).find((d) => d.key === fileKey),
      ),
      filter((d): d is APIM.AgentDocumentProjection => !!d),
    );
    const poll$ = timer(DOCUMENT_POLL_MS, DOCUMENT_POLL_MS).pipe(
      tap(() => this._sendAction.hydrate(sid)),
      ignoreElements(),
    );
    return document$.pipe(
      first(),
      timeout(ATTACH_TIMEOUT_MS),
      switchMap(() => merge(poll$, document$)),
      tap((doc) => {
        if (doc.progress?.total) {
          onReading(doc.progress.done / doc.progress.total);
        }
      }),
      first((doc) => doc.status !== 'processing'),
      timeout(READ_TIMEOUT_MS),
      map((doc) => {
        if (doc.status === 'failed') {
          throw new Error('The PDF could not be read');
        }
        onReading(1);
      }),
    );
  }

  private _session$(sid: string): Observable<SessionState> {
    return this._memoryStore.state$.pipe(
      map((state) => state.sessions[sid]),
      filter((s): s is SessionState => !!s),
    );
  }

  /** Adds each new card to the lesson until the agent's turn ends; errors if none were added. */
  private _addCardsAsTheyArrive(sid: string, lessonPK: string) {
    const seen = new Set<string>();
    let added = 0;
    return this._session$(sid).pipe(
      tap((s) => {
        // The server paused the turn (e.g. too many steps); carry on, as the chat panel does
        if (s.pendingResumeToolUseId) {
          const toolUseId = s.pendingResumeToolUseId;
          this._memoryStore.setPendingResume(sid, undefined);
          this._sendAction.resume(
            sid,
            toolUseId,
            undefined,
            'teacher',
            lessonPK,
          );
        }
        for (const card of s.protoCards) {
          // Later updates to a card already added are ignored
          if (seen.has(card.cardId) || !LESSON_CARD_TYPES.includes(card.type)) {
            continue;
          }
          seen.add(card.cardId);
          this._addCardAction
            .execute({card, target: {lessonPK, quiet: true}})
            .subscribe({
              next: () => added++,
              error: (err: unknown) =>
                console.warn(
                  `[BuildLessonFromPdf] Skipped a ${card.type} card:`,
                  err,
                ),
            });
        }
      }),
      takeWhile((s) => s.isStreaming || !!s.pendingResumeToolUseId, true),
      last(),
      timeout(BUILD_TIMEOUT_MS),
      map(() => {
        if (!added) {
          throw new Error('The agent made no cards for the lesson');
        }
        return added;
      }),
    );
  }
}
